/**
 * LoreCodexScene — the cozy codex of region lore (the 'Codex' contract key). A
 * beautiful, readable shelf of the legends, region history, and quiet tales the
 * world has revealed so far: the Mewtwo thread, the legendary birds, Mew, and the
 * small town stories. Strictly ambient and optional — nothing here is missable,
 * timed, or a checklist. Entries simply *appear* as the world fills, monotonically.
 *
 * The renderer owns no rules: which entries are unlocked is decided by the lore
 * layer (`unlockedEntries`), read from the live world through public session
 * accessors. Any story-director-grown entries on the save are merged in too, so the
 * shelf grows on its own over time without touching this scene.
 *
 * Two calm panes that reflow to any width (Layout + the UI kit): a scrolling list of
 * entry titles on the left/top, and the selected entry's prose on the right/bottom.
 * Touch-first throughout, with a clean Back exit.
 */
import Phaser from 'phaser';
import type { CodexEntry } from '@/shared';
import { unlockedEntries, type CodexWorldState } from '@/lore';
import { getSession } from './session.js';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET } from './ui/theme.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { ScrollList } from './ui/ScrollList.js';
import { domTextFieldFocused } from './ui/keyboardGate.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { stepSelection, initialSelection, type CodexDir } from './loreNav.js';
import type { GameSession } from '@/session';

/** A friendly label per codex kind, for the soft section tag on each entry. */
const KIND_LABEL: Record<CodexEntry['kind'], string> = {
  legend: 'Legend',
  region: 'Region',
  tale: 'Tale',
};

const CONTENT_MAX_WIDTH = 920;

export class LoreCodexScene extends Phaser.Scene {
  private session!: GameSession;
  private layout!: Layout;
  private reduced = false;

  private entries: CodexEntry[] = [];
  private selectedId: string | null = null;

  private heading?: Phaser.GameObjects.Text;
  private backButton?: Button;

  private listPanel?: Panel;
  private list?: ScrollList;
  private listRows: {
    id: string;
    container: Phaser.GameObjects.Container;
    bg: Phaser.GameObjects.Graphics;
    title: Phaser.GameObjects.Text;
    kind: Phaser.GameObjects.Text;
    rowW: number;
    rowH: number;
  }[] = [];

  private readerPanel?: Panel;
  private readerTitle?: Phaser.GameObjects.Text;
  private readerKind?: Phaser.GameObjects.Text;
  private readerScroll?: ScrollList;
  private readerBody?: Phaser.GameObjects.Text;
  private readerRelated?: Phaser.GameObjects.Text;
  private emptyText?: Phaser.GameObjects.Text;

  /** Bound so it can be removed on shutdown (Phaser reuses the scene instance). */
  private readonly keyHandler = (e: KeyboardEvent): void => this.onKey(e);
  private keyboardActive = false;

  constructor() {
    super('Codex');
  }

  create(): void {
    // Phaser REUSES the scene instance, so a re-entry runs create() again over the
    // prior visit's objects/handlers/arrays. Tear all of that down first, or text
    // stacks on text and stale row hit-areas swallow clicks (the click-through bug).
    this.teardown();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.session = getSession(this);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();

    this.entries = this.collectEntries();

    this.heading = this.add
      .text(0, 0, 'Codex', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xl}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    this.backButton = new Button(this, 0, 0, {
      label: '‹ Back',
      variant: 'ghost',
      width: 120,
      reducedMotion: this.reduced,
      onClick: () => goTo(this, 'Town'),
    });

    this.listPanel = new Panel(this, 0, 0, { width: 100, height: 100, fill: COLORS.surface });
    this.list = new ScrollList(this, 0, 0, { width: 100, height: 100 });

    this.readerPanel = new Panel(this, 0, 0, { width: 100, height: 100, fill: COLORS.surfaceAlt });
    this.readerKind = this.add.text(0, 0, '', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.xs}px`,
      color: CSS.accent,
      fontStyle: 'bold',
    });
    this.readerTitle = this.add.text(0, 0, '', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.lg}px`,
      color: CSS.warm,
      fontStyle: 'bold',
    });
    this.readerScroll = new ScrollList(this, 0, 0, { width: 100, height: 100 });
    this.readerBody = this.add.text(0, 0, '', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.md}px`,
      color: CSS.text,
      lineSpacing: 6,
    });
    this.readerRelated = this.add.text(0, 0, '', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.xs}px`,
      color: CSS.textFaint,
      fontStyle: 'italic',
    });
    this.readerScroll.content.add([this.readerBody, this.readerRelated]);

    if (this.entries.length === 0) {
      this.emptyText = this.add
        .text(
          0,
          0,
          'The codex is quiet for now.\nWander, befriend the town, and stories will find you.',
          {
            fontFamily: FONT.family,
            fontSize: `${FONT.size.md}px`,
            color: CSS.textMuted,
            align: 'center',
            lineSpacing: 6,
          },
        )
        .setOrigin(0.5);
    }

    this.buildListRows();
    const first = initialSelection(this.entryIds());
    if (first) this.select(first);

    this.relayout();
    this.layout.on('change', this.relayout, this);

    // Arrow-key navigation of the shelf (Up/Down move the highlight, Enter/Space
    // open). Touch/mouse taps still work via each row's POINTER_DOWN.
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.keyHandler);
      this.keyboardActive = true;
    }
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.teardown, this);

    fadeIn(this);
  }

  /** Ordered ids of the currently-shown entries (the keyboard/selection domain). */
  private entryIds(): string[] {
    return this.entries.map((e) => e.id);
  }

  /**
   * Tear down every object/handler/array from a prior visit so a re-entry starts
   * clean. Idempotent and safe to call before anything is built (first create()).
   */
  private teardown(): void {
    if (this.keyboardActive && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyboardActive = false;
    this.layout?.off('change', this.relayout, this);

    for (const row of this.listRows) row.container.destroy();
    this.listRows = [];

    this.heading?.destroy();
    this.backButton?.destroy();
    this.listPanel?.destroy();
    this.list?.destroy();
    this.readerPanel?.destroy();
    this.readerKind?.destroy();
    this.readerTitle?.destroy();
    this.readerScroll?.destroy();
    this.readerBody?.destroy();
    this.readerRelated?.destroy();
    this.emptyText?.destroy();

    this.heading = undefined;
    this.backButton = undefined;
    this.listPanel = undefined;
    this.list = undefined;
    this.readerPanel = undefined;
    this.readerKind = undefined;
    this.readerTitle = undefined;
    this.readerScroll = undefined;
    this.readerBody = undefined;
    this.readerRelated = undefined;
    this.emptyText = undefined;

    this.entries = [];
    this.selectedId = null;
  }

  /** Keyboard: arrows move the highlight; Enter re-affirms; Esc returns to Town. */
  private onKey(e: KeyboardEvent): void {
    if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
    // Esc always returns to Town, even with an empty shelf.
    if (e.key === 'Escape') {
      e.preventDefault();
      goTo(this, 'Town');
      return;
    }
    if (this.entries.length === 0) return;
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        this.step(-1);
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        this.step(1);
        break;
      case 'Enter':
      case ' ':
        // The detail already follows the highlight; Enter simply re-asserts it and
        // scrolls the reader back to the top, a calm "open this one".
        e.preventDefault();
        if (this.selectedId) this.select(this.selectedId);
        break;
      default:
        break;
    }
  }

  /** Move the highlighted entry by one (wrapping) and reveal it in the reader. */
  private step(dir: CodexDir): void {
    const next = stepSelection(this.entryIds(), this.selectedId, dir);
    if (next) {
      this.select(next);
      this.scrollListToSelection();
    }
  }

  /** Keep the highlighted row visible in the list viewport as the cursor moves. */
  private scrollListToSelection(): void {
    if (!this.list) return;
    const i = this.listRows.findIndex((r) => r.id === this.selectedId);
    if (i < 0) return;
    // Rows now have measured (variable) heights, so accumulate the offset to the target
    // row rather than assuming a fixed step.
    let offset = 0;
    for (let j = 0; j < i; j++) offset += this.listRows[j]!.rowH + SPACING.xs;
    const rowH = this.listRows[i]!.rowH;
    // Center the row in the list viewport (clamped inside ScrollList).
    this.list.scrollTo(offset - this.list.height / 2 + rowH / 2);
  }

  // ── data ────────────────────────────────────────────────────────────────────

  /**
   * Unlocked seed entries (decided by the lore layer from the live world) merged
   * with any story-grown entries the save carries. Deduped by id, seeds first.
   */
  private collectEntries(): CodexEntry[] {
    const world = this.worldState();
    const seeds = unlockedEntries(world);

    // The session keeps grown lore privately; read it defensively so future
    // story-director output appears here with no code change, never crashing if absent.
    const grown = readGrownEntries(this.session);

    const byId = new Map<string, CodexEntry>();
    for (const e of seeds) byId.set(e.id, e);
    for (const e of grown) if (!byId.has(e.id)) byId.set(e.id, e);
    return [...byId.values()];
  }

  /** Build the (ambient) unlock world-state from public session accessors. */
  private worldState(): CodexWorldState {
    const friendshipTotal = this.session.society.reduce(
      (sum, t) => sum + (t.friendshipWithPlayer || 0),
      0,
    );
    return {
      visitedNodeIds: [],
      caughtSpeciesIds: [...this.session.dex.caught],
      rumorCounts: {},
      friendshipTotal,
    };
  }

  // ── list ────────────────────────────────────────────────────────────────────

  /**
   * The pixel height a shelf row needs for a title wrapped to `rowW` plus the kind tag
   * below it. MEASURE-TO-CONTENT: a long codex title (e.g. "The Little Dream at the Root
   * of Everything") wraps to 2–3 lines on a ~360px phone list pane, which a fixed 64px
   * row could not hold — the title ran onto the kind tag and the next row. Sizing each
   * row to its own wrapped title keeps them clear at any width.
   */
  private rowHeightFor(titleH: number): number {
    return Math.max(64, Math.ceil(SPACING.sm + titleH + SPACING.xs + FONT.size.xs + SPACING.sm));
  }

  private buildListRows(): void {
    this.listRows = [];
    if (!this.list) return;
    const rowW = 240;
    let y = 0;
    for (const entry of this.entries) {
      const title = this.add.text(0, 0, entry.title, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.text,
        fontStyle: 'bold',
        wordWrap: { width: rowW - SPACING.md * 2 },
      });
      const rowH = this.rowHeightFor(title.height);
      const container = this.add.container(0, y + rowH / 2);
      const bg = this.add.graphics();
      title.setPosition(-rowW / 2 + SPACING.md, -rowH / 2 + SPACING.sm);
      const kind = this.add.text(
        -rowW / 2 + SPACING.md,
        rowH / 2 - SPACING.sm - FONT.size.xs,
        KIND_LABEL[entry.kind],
        {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
        },
      );
      container.add([bg, title, kind]);
      // The row CONTAINER is the hit target, with an EXPLICIT centered rect matching
      // its drawn bounds — exactly the DexScene tile pattern that hit-tests correctly
      // through the ScrollList's transform + geometry mask (a geometry mask is
      // render-only; it does NOT block input hit-testing). We deliberately do NOT call
      // container.setSize(): on a Phaser Container, setSize sets displayOrigin = w/2 and
      // shifts the hit point, re-offsetting the very rect we are centering. With no
      // setSize the displayOrigin is 0, so the centered rect maps 1:1 to the visible row.
      // The prior bare-default and leaf-Zone attempts mis-aligned the hit area; this is
      // the click-through fix. Tapping ANY row drives select() -> repaint + reader reload.
      container.setInteractive(
        new Phaser.Geom.Rectangle(-rowW / 2, -rowH / 2, rowW, rowH),
        Phaser.Geom.Rectangle.Contains,
      );
      if (container.input) container.input.cursor = 'pointer';
      // Select on TAP (POINTER_UP), and no-op if the gesture was a drag-scroll — so a
      // touch-drag that started over this row scrolls the list instead of selecting it.
      const list = this.list;
      container.on(Phaser.Input.Events.POINTER_UP, () => {
        if (!list.wasDragging()) this.select(entry.id);
      });

      this.list.addItem(
        container as Phaser.GameObjects.Container & { y: number },
        rowH + SPACING.xs,
      );
      this.listRows.push({ id: entry.id, container, bg, title, kind, rowW, rowH });
      y += rowH + SPACING.xs;
    }
    this.list.setContentHeight(y);
    this.paintRows();
  }

  /** Repaint every row's selection highlight using its own current width + height. */
  private paintRows(): void {
    for (const row of this.listRows) {
      const rowW = row.rowW;
      const rowH = row.rowH;
      const selected = row.id === this.selectedId;
      row.bg.clear();
      // Highlighted row: a soft filled band + a warm accent stripe down its left edge,
      // so the selection (keyboard or tap) is always clearly visible.
      row.bg.fillStyle(selected ? COLORS.surfaceAlt : COLORS.surface, selected ? 1 : 0);
      row.bg.fillRoundedRect(-rowW / 2, -rowH / 2, rowW, rowH, RADIUS.md);
      row.title.setColor(selected ? CSS.warm : CSS.text);
      if (selected) {
        row.bg.fillStyle(COLORS.accent, 1);
        row.bg.fillRoundedRect(-rowW / 2, -rowH / 2 + SPACING.xs, 3, rowH - SPACING.sm, RADIUS.sm);
      }
    }
  }

  private select(id: string): void {
    this.selectedId = id;
    const entry = this.entries.find((e) => e.id === id);
    if (!entry) return;
    this.paintRows();

    this.readerKind?.setText(KIND_LABEL[entry.kind].toUpperCase());
    this.readerTitle?.setText(entry.title);
    // Set the prose itself so the reader pane actually shows the entry (layoutReader
    // re-flows + re-sets it too, but set it here so a selection is never blank).
    this.readerBody?.setText(entry.body);

    const related = this.relatedNames(entry);
    this.readerRelated?.setText(related ? `Mentioned: ${related}` : '');

    this.layoutReader();
  }

  private relatedNames(entry: CodexEntry): string {
    if (entry.relatedSpeciesIds.length === 0) return '';
    const index = this.session.bundle.index;
    const names = entry.relatedSpeciesIds.map((id) => index.speciesById(id)?.name ?? id);
    return names.join(', ');
  }

  // ── layout ────────────────────────────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const headerY = safe.y + SPACING.xl;
    const w = Math.min(CONTENT_MAX_WIDTH, safe.width - SPACING.lg);

    this.heading?.setPosition(cx - w / 2, headerY);
    this.backButton?.setPosition(cx + w / 2 - (this.backButton?.buttonWidth ?? 120) / 2, headerY);

    const top = headerY + SPACING.xl + SPACING.sm;
    const bottom = safe.y + safe.height - SPACING.lg;
    const areaH = Math.max(TOUCH_TARGET, bottom - top);

    // Empty state: center a gentle message, hide the panes.
    const hasEntries = this.entries.length > 0;
    this.listPanel?.setVisible(hasEntries);
    this.list?.setVisible(hasEntries);
    this.readerPanel?.setVisible(hasEntries);
    this.readerScroll?.setVisible(hasEntries);
    this.readerTitle?.setVisible(hasEntries);
    this.readerKind?.setVisible(hasEntries);
    if (!hasEntries) {
      this.emptyText?.setPosition(cx, top + areaH / 2);
      return;
    }

    const stacked = w < 720;
    const gap = SPACING.md;
    const left = cx - w / 2;

    if (stacked) {
      // Phone: a short list on top, the reader filling the rest.
      const listH = Math.min(220, Math.round(areaH * 0.4));
      const readerH = areaH - listH - gap;
      this.placeListPane(cx, top + listH / 2, w, listH);
      this.placeReaderPane(cx, top + listH + gap + readerH / 2, w, readerH);
    } else {
      // Desktop: list column beside the reader.
      const listW = Math.min(280, Math.round(w * 0.34));
      const readerW = w - listW - gap;
      const listCx = left + listW / 2;
      const readerCx = left + listW + gap + readerW / 2;
      this.placeListPane(listCx, top + areaH / 2, listW, areaH);
      this.placeReaderPane(readerCx, top + areaH / 2, readerW, areaH);
    }
  }

  private placeListPane(cx: number, cy: number, w: number, h: number): void {
    this.listPanel?.setPosition(cx, cy).resize(w, h);
    if (this.list) {
      this.list.setPosition(cx, cy);
      this.list.resize(w - SPACING.sm, h - SPACING.sm);
    }
    // Re-flow rows to the pane width.
    const rowW = w - SPACING.lg;
    for (const row of this.listRows) row.rowW = rowW;
    this.reflowRows(rowW);
  }

  private reflowRows(rowW: number): void {
    let y = 0;
    for (const row of this.listRows) {
      // Re-wrap the title to the new pane width, then RE-MEASURE the row height so a
      // title that now wraps to more/fewer lines never overlaps its kind tag or the
      // next row (the narrow-portrait root bug). Re-stack the rows by cumulative height.
      row.title.setWordWrapWidth(rowW - SPACING.md * 2);
      const rowH = this.rowHeightFor(row.title.height);
      row.rowH = rowH;
      row.title.setPosition(-rowW / 2 + SPACING.md, -rowH / 2 + SPACING.sm);
      row.kind.setPosition(-rowW / 2 + SPACING.md, rowH / 2 - SPACING.sm - FONT.size.xs);
      row.container.setY(y + rowH / 2);
      // The explicit hit rect doesn't auto-resize, so mutate it in place to keep the
      // clickable region exactly on the (re-sized) visible row. We do NOT call
      // container.setSize here — that would set displayOrigin = w/2 and re-offset the
      // hit point (see buildListRows). The centered rect with displayOrigin 0 is correct.
      const hit = row.container.input?.hitArea;
      if (hit instanceof Phaser.Geom.Rectangle) {
        hit.setTo(-rowW / 2, -rowH / 2, rowW, rowH);
      }
      y += rowH + SPACING.xs;
    }
    this.list?.setContentHeight(y);
    // Re-draw the highlight bands at the new width/height (single source of truth).
    this.paintRows();
  }

  private placeReaderPane(cx: number, cy: number, w: number, h: number): void {
    this.readerPanel?.setPosition(cx, cy).resize(w, h);
    const innerW = w - SPACING.lg * 2;
    const topY = cy - h / 2 + SPACING.lg;

    this.readerKind?.setPosition(cx - innerW / 2, topY);
    const titleY = topY + (this.readerKind?.height ?? 12) + SPACING.xs;
    this.readerTitle?.setPosition(cx - innerW / 2, titleY);
    this.readerTitle?.setWordWrapWidth(innerW);

    const bodyTop = titleY + (this.readerTitle?.height ?? 24) + SPACING.md;
    const bodyBottom = cy + h / 2 - SPACING.lg;
    const bodyH = Math.max(TOUCH_TARGET, bodyBottom - bodyTop);
    if (this.readerScroll) {
      this.readerScroll.setPosition(cx, bodyTop + bodyH / 2);
      this.readerScroll.resize(innerW, bodyH);
    }
    this.readerBody?.setWordWrapWidth(innerW);
    this.layoutReader();
  }

  /** Re-flow the reader body + related line into the reader scroll content. */
  private layoutReader(): void {
    const entry = this.entries.find((e) => e.id === this.selectedId);
    if (!entry || !this.readerBody) return;
    // Show the prose (this is what makes the reader pane non-empty).
    this.readerBody.setText(entry.body);
    const innerW = this.readerBody.style.wordWrapWidth ?? 360;
    // ScrollList maps content-local y=0 to the viewport top (its content container is
    // already shifted up by vpHeight/2), so stack from 0 — NOT from -h/2, which would
    // push the prose a whole viewport above the mask and leave the pane blank.
    const top = SPACING.xs;
    this.readerBody.setPosition(-innerW / 2, top);
    let contentH = this.readerBody.height;
    if (this.readerRelated) {
      const related = this.relatedNames(entry);
      this.readerRelated.setText(related ? `Mentioned: ${related}` : '');
      this.readerRelated.setWordWrapWidth(innerW);
      this.readerRelated.setPosition(-innerW / 2, top + this.readerBody.height + SPACING.md);
      contentH += SPACING.md + (related ? this.readerRelated.height : 0);
    }
    this.readerScroll?.setContentHeight(top + contentH + SPACING.lg);
    this.readerScroll?.scrollTo(0);
  }
}

/**
 * Read story-director-grown codex entries off the session, if it exposes them. The
 * session keeps `lore` private today; this guarded probe lets grown threads surface
 * automatically once a public accessor exists, and is a harmless no-op until then.
 */
function readGrownEntries(session: GameSession): readonly CodexEntry[] {
  const maybe = session as unknown as {
    lore?: { entries?: readonly CodexEntry[] };
    getLore?: () => { entries?: readonly CodexEntry[] };
  };
  const lore = maybe.getLore ? maybe.getLore() : maybe.lore;
  const entries = lore?.entries;
  return Array.isArray(entries) ? entries : [];
}
